import { Component, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidationErrors, AbstractControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { CompaniesService } from '../../../../core/api/companies.service';
import { GetPlansQueryResponse } from '../../../../core/model/getPlansQueryResponse';
import { GetCountriesQueryResponse } from '../../../../core/model/getCountriesQueryResponse';
import { GetLanguagesQueryResponse } from '../../../../core/model/getLanguagesQueryResponse';
import { OrganizationSummaryResponse } from '../../../../core/model/organizationSummaryResponse';
import { CreateOrganizationRequest } from '../../../../core/model/createOrganizationRequest';
import { StartCompanyOnboardingRequest } from '../../../../core/model/startCompanyOnboardingRequest';
import { StartCompanyOnboardingResponse } from '../../../../core/model/startCompanyOnboardingResponse';
import { JoinSharedChannelRequest } from '../../../../core/model/joinSharedChannelRequest';
import { ChannelMemberRole } from '../../../../core/model/channelMemberRole';
import { LanguageRequest } from '../../../../core/model/languageRequest';
import { ChannelsService } from '../../../../core/api/channels.service';
import { ChannelResponse } from '../../../../core/model/channelResponse';
import { InviteUsersToCompanyRequest } from '../../../../core/model/inviteUsersToCompanyRequest';
import { SetDataOperatorStatusRequest } from '../../../../core/model/setDataOperatorStatusRequest';
import { MatStepper } from '@angular/material/stepper';
import { ErrorTranslationService } from '../../../../core/services/errors/error-translation.service';
import { ErrorService } from '../../../../core/services/errors/errorsService';
import { AssignPlanToCompanyRequest } from '../../../../core/model/assignPlanToCompanyRequest';
import { PlannProfile } from '../../../../core/model/plannProfile';
import { CompanyPlanLimitsService } from '../../../../core/services/company-plan-limits.service';
import { map, of, switchMap, throwError } from 'rxjs';

// Interfaz extendida para manejar estados UI de los canales
interface ChannelWithState extends ChannelResponse {
  isJoining?: boolean;
  isJoined?: boolean;
}

@Component({
  selector: 'app-new-company-dialog',
  templateUrl: './new-company-dialog.component.html',
  styleUrls: ['./new-company-dialog.component.scss']
})
export class NewCompanyDialogComponent implements OnInit, OnDestroy {
  private _formBuilder = inject(FormBuilder);
  private companiesService = inject(CompaniesService);
  private channelsService = inject(ChannelsService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private errorTranslationService = inject(ErrorTranslationService);
  private errorService = inject(ErrorService);
  private companyPlanLimitsService = inject(CompanyPlanLimitsService);

  @ViewChild('stepper') stepper!: MatStepper;

  isLinear = true;
  progress = 0;
  createdCompanyId: string | null = null;
  isCreatingCompany = false;
  isAssigningOrganization = false;
  plans: GetPlansQueryResponse[] = [];
  filteredPlans: GetPlansQueryResponse[] = [];
  isLoadingPlans = false;
  isAssigningPlan = false;
  selectedPlan?: GetPlansQueryResponse;
  selectedProfileFilter: string = 'all';
  profileFilters = [
    { value: 'all', labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_3.PROFILE_FILTERS.ALL' },
    { value: 'Hunter', labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_3.PROFILE_FILTERS.HUNTER' },
    { value: 'Deliver', labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_3.PROFILE_FILTERS.DELIVER' },
    { value: 'Hunter_Deliver', labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_3.PROFILE_FILTERS.HUNTER_DELIVER' },
    { value: 'eB2B', labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_3.PROFILE_FILTERS.EB2B' },
    { value: 'Custom', labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_3.PROFILE_FILTERS.CUSTOM' }
  ];

  countries: GetCountriesQueryResponse[] = [];
  languages: GetLanguagesQueryResponse[] = [];
  isLoadingCountries = false;
  isLoadingLanguages = false;

  // Idiomas seleccionados para producto (paso 1)
  enabledProductLanguages: GetLanguagesQueryResponse[] = [];
  selectedProductLanguages: GetLanguagesQueryResponse[] = [];
  activeProductLanguageCodes: Set<string> = new Set();

  // Organizaciones
  organizations: OrganizationSummaryResponse[] = [];
  filteredOrganizations: OrganizationSummaryResponse[] = [];
  selectedOrganization: OrganizationSummaryResponse | null = null;
  isLoadingOrganizations = false;
  organizationSearchText: string = '';
  selectedOrganizationIndex: number = -1;

  // Roles como enum con flags (bitwise)
  roles = [
    { value: 1, labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_1.ROLES_OPTIONS.MANUFACTURER' },
    { value: 2, labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_1.ROLES_OPTIONS.DISTRIBUTOR' },
    { value: 4, labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_1.ROLES_OPTIONS.PURCHASING_CENTER' },
    { value: 8, labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_1.ROLES_OPTIONS.RETAILER' },
    { value: 16, labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_1.ROLES_OPTIONS.OPERATOR' },
    { value: 32, labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_1.ROLES_OPTIONS.EB2B' },
    { value: 64, labelKey: 'ADMINISTRATION.DIALOGS.NEW_COMPANY.STEP_1.ROLES_OPTIONS.CASH_AND_CARRY' }
  ];

  // Formularios
  firstFormGroup!: FormGroup;
  secondFormGroup!: FormGroup;
  thirdFormGroup!: FormGroup;
  fourthFormGroup!: FormGroup;
  fifthFormGroup!: FormGroup;

  // Formulario para crear organización
  createOrganizationForm!: FormGroup;
  showCreateOrganizationForm = false;
  isCreatingOrganization = false;

  // Canales B2B con estados extendidos
  channelsB2B: ChannelWithState[] = [];
  isLoadingChannels = false;
  hasMoreChannels = false;
  channelsContinuationToken: string = '';

  // Paso 5: Invitar usuarios
  emailsList: string[] = [];
  isInvitingUsers = false;

  constructor(
    private dialogRef: MatDialogRef<NewCompanyDialogComponent>,
    private translateService: TranslateService,
    private connectorsService: CompaniesService
  ) { }

  ngOnInit(): void {
    // Cargar traducciones de errores de forma lazy
    this.errorTranslationService.loadErrorTranslations();
    
    this.initForms();
    this.loadCountries();
    this.loadLanguages();
    this.loadOrganizations();
    this.loadPlans();
    this.loadChannelsB2B();

    // Escuchar cambios en el stepper
    setTimeout(() => {
      if (this.stepper) {
        this.stepper.selectionChange.subscribe((event) => {
          this.onStepChange(event.previouslySelectedIndex, event.selectedIndex);
        });
      }
    });
  }

  ngOnDestroy(): void { }

  initForms(): void {
    // Paso 1: Crear Empresa (0% → 20%)
    this.firstFormGroup = this._formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      legalName: ['', [Validators.required, Validators.minLength(3)]],
      legalId: ['', [Validators.required, Validators.minLength(5)]],
      countryCode: ['', Validators.required],
      langCode: ['', Validators.required],
      role: [[], Validators.required],
      syncWithV1: [false],
      enableAlignments: [false],
      canShareData: [false],
      canConsumeData: [false],
      isDataOperator: [false]
    }, { validators: (control) => this.validateProductLanguages(control) });

    // Regla de negocio: Si syncWithV1 = true, enableAlignments se fuerza a true
    this.firstFormGroup.get('syncWithV1')?.valueChanges.subscribe(value => {
      if (value) {
        this.firstFormGroup.get('enableAlignments')?.setValue(true);
        this.firstFormGroup.get('enableAlignments')?.disable();
      } else {
        this.firstFormGroup.get('enableAlignments')?.enable();
      }
    });

    // Regla de negocio: Operador de Datos solo aplica si la empresa comparte datos
    this.firstFormGroup.get('canShareData')?.valueChanges.subscribe(value => {
      if (!value) {
        this.firstFormGroup.get('isDataOperator')?.setValue(false);
      }
    });

    // Paso 2: Asignar Organización (20% → 40%)
    this.secondFormGroup = this._formBuilder.group({
      belongsToOrganization: ['no', Validators.required],
      organizationId: ['']
    });

    // Observar cambios en belongsToOrganization
    this.secondFormGroup.get('belongsToOrganization')?.valueChanges.subscribe(value => {
      if (value === 'yes') {
        this.secondFormGroup.get('organizationId')?.setValidators([Validators.required]);
      } else {
        this.secondFormGroup.get('organizationId')?.clearValidators();
        this.secondFormGroup.get('organizationId')?.setValue('');
        this.selectedOrganization = null;
      }
      this.secondFormGroup.get('organizationId')?.updateValueAndValidity();
    });

    // Paso 3: Asignar Plan (40% → 60%)
    this.thirdFormGroup = this._formBuilder.group({
      planId: ['', Validators.required],
      profile: [0]
    });

    // Paso 4: Unirse a Canales (60% → 80%) - Sin formulario necesario
    this.fourthFormGroup = this._formBuilder.group({});

    // Paso 5: Invitar Usuario (80% → 100%)
    this.fifthFormGroup = this._formBuilder.group({
      inviteChoice: ['no', Validators.required],
      emails: ['', [this.emailsValidator.bind(this)]]
    });

    // Escuchar cambios en el campo de emails
    this.fifthFormGroup.get('emails')?.valueChanges.subscribe(value => {
      this.parseEmails(value);
    });

    // Limpiar correos si el usuario decide no invitar
    this.fifthFormGroup.get('inviteChoice')?.valueChanges.subscribe(choice => {
      if (choice === 'no') {
        this.emailsList = [];
        this.fifthFormGroup.get('emails')?.setValue('');
      }
    });

    // Formulario para crear organización
    this.createOrganizationForm = this._formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      legalName: ['', [Validators.required]],
      legalId: ['', [Validators.required]],
      country: ['', [Validators.required]],
      email: ['', [Validators.email]],
      phoneNumber: [''],
      website: [''],
      address: ['']
    });
  }

  //INI Cargar datos auxiliares

  loadCountries(): void {
    this.isLoadingCountries = true;

    this.companiesService.companiesGetCounties(this.translate.currentLang || 'es').subscribe({
      next: (countries) => {
        this.countries = countries || [];
        this.isLoadingCountries = false;
      },
      error: (error) => {
        console.error('Error al cargar países:', error);
        this.isLoadingCountries = false;

        this.snackBar.open(
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.ERRORS.LOAD_COUNTRIES'),
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  loadLanguages(): void {
    this.isLoadingLanguages = true;

    this.companiesService.companiesGetLanguages(this.translate.currentLang || 'es').subscribe({
      next: (languages) => {
        this.languages = languages || [];
        // Filtrar idiomas habilitados para datos de producto
        this.enabledProductLanguages = this.languages.filter(lang => lang.isEnabledForProductData);
        this.isLoadingLanguages = false;
      },
      error: (error) => {
        console.error('Error al cargar idiomas:', error);
        this.isLoadingLanguages = false;

        this.snackBar.open(
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.ERRORS.LOAD_LANGUAGES'),
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  loadOrganizations(): void {
    this.isLoadingOrganizations = true;

    this.companiesService.v1CompaniesApiOrganizationsGet().subscribe({
      next: (organizations) => {
        this.organizations = organizations || [];
        this.filteredOrganizations = [...this.organizations];
        this.isLoadingOrganizations = false;
      },
      error: (error) => {
        console.error('Error al cargar organizaciones:', error);
        this.isLoadingOrganizations = false;

        this.snackBar.open(
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.ERRORS.LOAD_ORGANIZATIONS'),
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  loadPlans(): void {
    this.isLoadingPlans = true;

    this.companiesService.companiesGetPlans(this.translate.currentLang || 'es').subscribe({
      next: (plans) => {
        this.plans = plans || [];
        this.filteredPlans = [...this.plans];
        this.isLoadingPlans = false;
      },
      error: (error) => {
        console.error('Error al cargar planes:', error);
        this.isLoadingPlans = false;

        this.snackBar.open(
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.ERRORS.LOAD_PLANS'),
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  loadChannelsB2B(): void {
    if (this.isLoadingChannels) return;

    this.isLoadingChannels = true;

    this.channelsService.channelsGetCooperativeSharedChannelsPaginated(this.translateService.currentLang, 20, this.channelsContinuationToken).subscribe({
      next: (data: any) => {
        const newChannels = (data.channels || []).map((channel: ChannelResponse) => ({
          ...channel,
          isJoining: false,
          isJoined: false
        } as ChannelWithState));

        this.channelsB2B = [...this.channelsB2B, ...newChannels];
        this.channelsContinuationToken = data.continuationToken ? JSON.stringify(data.continuationToken) : '';
        this.hasMoreChannels = !!data.continuationToken;
        this.isLoadingChannels = false;
      },
      error: (error) => {
        console.error('Error al cargar canales:', error);
        this.isLoadingChannels = false;

        this.snackBar.open(
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.ERRORS.LOAD_CHANNELS'),
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  //FIN Cargar datos auxiliares

  onChannelsScroll(event: any): void {
    const scrollPosition = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const clientHeight = event.target.clientHeight;

    if (scrollPosition + clientHeight >= scrollHeight - 10) {
      this.onLoadMoreChannels();
    }
  }

  /**
   * Unirse a un canal compartido
   * @param channel Canal a unirse
   */
  joinChannel(channel: ChannelWithState): void {
    if (!this.createdCompanyId || channel.isJoining) {
      return;
    }

    channel.isJoining = true;

    const request: JoinSharedChannelRequest = {
      role: ChannelMemberRole.Publisher,
      category: channel.category as any
    };

    this.channelsService.channelsCompanyJoinSharedChannel(
      channel.id!,
      this.createdCompanyId,
      request
    ).subscribe({
      next: () => {
        channel.isJoining = false;
        channel.isJoined = true;

      },
      error: (error) => {
        console.error(`Error al unirse al canal ${channel.name}:`, error);
        channel.isJoining = false;

        this.snackBar.open(
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.ERRORS.JOIN_CHANNEL', { channelName: channel.name }),
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  onLoadMoreChannels(): void {
    if (this.isLoadingChannels || !this.hasMoreChannels) return;
    this.loadChannelsB2B();
  }

  filterOrganizations(): void {
    const searchTerm = this.organizationSearchText.toLowerCase().trim();

    if (!searchTerm) {
      this.filteredOrganizations = [...this.organizations];
    } else {
      this.filteredOrganizations = this.organizations.filter(org =>
        org.name?.toLowerCase().includes(searchTerm) ||
        org.legalName?.toLowerCase().includes(searchTerm) ||
        org.legalId?.toLowerCase().includes(searchTerm)
      );
    }

    this.selectedOrganizationIndex = -1;
  }

  selectOrganization(organization: OrganizationSummaryResponse): void {
    this.selectedOrganization = organization;
    this.secondFormGroup.get('organizationId')?.setValue(organization.id);
  }

  onOrganizationKeyDown(event: KeyboardEvent): void {
    if (this.filteredOrganizations.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedOrganizationIndex = Math.min(
          this.selectedOrganizationIndex + 1,
          this.filteredOrganizations.length - 1
        );
        this.scrollToSelectedOrganization();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedOrganizationIndex = Math.max(this.selectedOrganizationIndex - 1, 0);
        this.scrollToSelectedOrganization();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.selectedOrganizationIndex >= 0 && this.selectedOrganizationIndex < this.filteredOrganizations.length) {
          this.selectOrganization(this.filteredOrganizations[this.selectedOrganizationIndex]);
        }
        break;
    }
  }

  private scrollToSelectedOrganization(): void {
    setTimeout(() => {
      const container = document.querySelector('.organizations-list-container');
      const selectedElement = document.querySelector('.organization-item.keyboard-selected');
      if (container && selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }

  onOrganizationMouseEnter(index: number): void {
    this.selectedOrganizationIndex = index;
  }

  filterPlansByProfile(profile: string): void {
    this.selectedProfileFilter = profile;

    if (profile === 'all') {
      this.filteredPlans = [...this.plans];
    } else {
      this.filteredPlans = this.plans.filter(plan =>
        plan.profile?.toLowerCase() === profile.toLowerCase()
      );
    }
  }

  selectPlan(planId: string): void {
    this.thirdFormGroup.patchValue({ planId: planId });
    // Buscar y asignar el plan seleccionado
    const foundPlan = this.plans.find(p => p.id === planId);
    if (foundPlan) {
      this.selectedPlan = foundPlan;
    }
  }

  toggleCreateOrganizationForm(): void {
    this.showCreateOrganizationForm = !this.showCreateOrganizationForm;

    if (!this.showCreateOrganizationForm) {
      // Resetear el formulario al cerrar
      this.createOrganizationForm.reset();
    }
  }

  createOrganization(): void {
    if (this.createOrganizationForm.invalid || this.isCreatingOrganization) {
      return;
    }

    this.isCreatingOrganization = true;

    const request: CreateOrganizationRequest = {
      name: this.createOrganizationForm.get('name')?.value,
      legalName: this.createOrganizationForm.get('legalName')?.value,
      legalId: this.createOrganizationForm.get('legalId')?.value,
      country: this.createOrganizationForm.get('country')?.value,
      email: this.createOrganizationForm.get('email')?.value,
      phoneNumber: this.createOrganizationForm.get('phoneNumber')?.value,
      website: this.createOrganizationForm.get('website')?.value,
      address: this.createOrganizationForm.get('address')?.value
    };

    this.companiesService.v1CompaniesApiOrganizationsPost(request).subscribe({
      next: (organizationId) => {

        // Recargar la lista de organizaciones
        this.loadOrganizations();

        // Cerrar el formulario de creación
        this.showCreateOrganizationForm = false;
        this.createOrganizationForm.reset();

        this.isCreatingOrganization = false;
      },
      error: (error) => {
        console.error('Error al crear organización:', error);
        this.isCreatingOrganization = false;

        this.snackBar.open(
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.ERRORS.CREATE_ORGANIZATION'),
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  onEmailsPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const currentValue = this.fifthFormGroup.get('emails')?.value || '';

    // Procesar el texto pegado
    const processedText = this.processEmailsText(pastedText);

    // Combinar con el valor actual si existe
    const newValue = currentValue
      ? currentValue + '\n' + processedText
      : processedText;

    this.fifthFormGroup.get('emails')?.setValue(newValue);
  }

  private processEmailsText(text: string): string {
    // Reemplazar comas, puntos y comas, y espacios múltiples por saltos de línea
    return text
      .replace(/[,;]/g, '\n')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('\n');
  }

  private parseEmails(text: string): void {
    if (!text) {
      this.emailsList = [];
      return;
    }

    // Extraer emails del texto
    const emails = text
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0 && this.isValidEmail(email));

    // Eliminar duplicados
    this.emailsList = [...new Set(emails)];
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private emailsValidator(control: any): { [key: string]: any } | null {
    const text = control.value;
    if (!text) {
      return null;
    }

    const emails = text
      .split(/[\n,;]/)
      .map((email: string) => email.trim())
      .filter((email: string) => email.length > 0);

    const invalidEmails = emails.filter((email: string) => !this.isValidEmail(email));

    if (invalidEmails.length > 0) {
      return { invalidEmails: true };
    }

    return null;
  }

  removeEmail(index: number): void {
    this.emailsList.splice(index, 1);

    // Actualizar el valor del textarea
    const newValue = this.emailsList.join('\n');
    this.fifthFormGroup.get('emails')?.setValue(newValue);
  }

  /**
   * Alternar selección de idioma para datos de producto
   */
  toggleProductLanguage(language: GetLanguagesQueryResponse): void {
    const index = this.selectedProductLanguages.findIndex(l => l.code === language.code);
    if (index >= 0) {
      this.selectedProductLanguages.splice(index, 1);
      // Si el idioma eliminado estaba marcado como activo, eliminar de activos
      this.activeProductLanguageCodes.delete(language.code!);
    } else {
      this.selectedProductLanguages.push(language);
    }
    // Forzar revalidación del formulario
    this.firstFormGroup.updateValueAndValidity();
  }

  /**
   * Verificar si un idioma está seleccionado
   */
  isLanguageSelected(language: GetLanguagesQueryResponse): boolean {
    return this.selectedProductLanguages.some(l => l.code === language.code);
  }

  /**
   * Alternar idioma activo (permite múltiples idiomas activos)
   */
  toggleActiveProductLanguage(languageCode: string): void {
    if (this.activeProductLanguageCodes.has(languageCode)) {
      this.activeProductLanguageCodes.delete(languageCode);
    } else {
      this.activeProductLanguageCodes.add(languageCode);
    }
    // Forzar revalidación del formulario
    this.firstFormGroup.updateValueAndValidity();
  }

  /**
   * Verificar si un idioma está marcado como activo
   */
  isLanguageActive(languageCode: string): boolean {
    return this.activeProductLanguageCodes.has(languageCode);
  }

  /**
   * Validar que haya al menos un idioma seleccionado y uno marcado como activo
   */
  validateProductLanguages(control: AbstractControl): ValidationErrors | null {
    if (this.selectedProductLanguages.length === 0 || this.activeProductLanguageCodes.size === 0) {
      return { productLanguagesInvalid: true };
    }
    return null;
  }

  /**
   * Construir array de LanguageRequest desde los idiomas seleccionados
   */
  private buildLanguageRequests(): LanguageRequest[] {
    return this.selectedProductLanguages.map(lang => ({
      code: lang.code,
      name: lang.name,
      isActive: this.activeProductLanguageCodes.has(lang.code!)
    }));
  }

  /**
   * Convierte un array de roles (flags) a un número bitwise
   */
  private convertRolesToBitwise(roles: number[]): number {
    if (!roles || roles.length === 0) {
      return 0;
    }
    return roles.reduce((acc, role) => acc | role, 0);
  }

  /**
   * Construye el DTO StartCompanyOnboardingRequest a partir de los datos de los formularios
   */
  private buildOnboardingRequest(): StartCompanyOnboardingRequest {
    // Datos del paso 1
    const name = this.firstFormGroup.get('name')?.value;
    const legalName = this.firstFormGroup.get('legalName')?.value;
    const legalId = this.firstFormGroup.get('legalId')?.value;
    const country = this.firstFormGroup.get('countryCode')?.value;
    const langCode = this.firstFormGroup.get('langCode')?.value;
    const rolesArray = this.firstFormGroup.get('role')?.value || [];
    const role = this.convertRolesToBitwise(rolesArray);

    // Datos del paso 2 (organización)
    const belongsToOrg = this.secondFormGroup.get('belongsToOrganization')?.value;
    const organizationId = belongsToOrg === 'yes' ? this.secondFormGroup.get('organizationId')?.value : null;

    // Datos del paso 3 (plan)
    const planId = this.thirdFormGroup.get('planId')?.value;

    // Construir array de idiomas
    const languages = this.buildLanguageRequests();

    // Datos del paso 1 (sincronización y alineaciones)
    const synchronizeWithV1 = this.firstFormGroup.get('syncWithV1')?.value ?? false;
    // getRawValue para obtener el valor aunque esté disabled
    const alignments = this.firstFormGroup.getRawValue().enableAlignments ?? false;

    // Datos del paso 1 (compartir y consultar datos)
    const canShareData = this.firstFormGroup.get('canShareData')?.value ?? false;
    const canConsumeData = this.firstFormGroup.get('canConsumeData')?.value ?? false;

    const request: StartCompanyOnboardingRequest = {
      name: name,
      legalName: legalName,
      legalId: legalId,
      country: country,
      langCode: langCode,
      role: role,
      planId: planId,
      organizationId: organizationId,
      languages: languages.length > 0 ? languages : null,
      address: null,
      phoneNumber: null,
      email: null,
      webSite: null,
      synchronizeWithV1: synchronizeWithV1,
      alignments: alignments,
      canShareData: canShareData,
      canConsumeData: canConsumeData
    };

    return request;
  }

  private buildAssignPlanRequest(plan: GetPlansQueryResponse): AssignPlanToCompanyRequest {
    return {
      planId: plan.id,
      profile: this.toPlanProfile(plan.profile),
      customLimits: plan.limits ? { ...plan.limits } : undefined
    };
  }

  private getSelectedPlan(): GetPlansQueryResponse | undefined {
    const planId = this.thirdFormGroup.get('planId')?.value;
    return this.plans.find((plan) => plan.id === planId);
  }

  private toPlanProfile(profile: string | null | undefined): PlannProfile {
    const profiles: PlannProfile[] = ['Hunter', 'Deliver', 'eB2B', 'Custom'];
    return profiles.includes(profile as PlannProfile) ? profile as PlannProfile : 'Custom';
  }

  /**
   * Inicia el proceso de onboarding de la empresa
   * Se ejecuta al finalizar el paso 3 (Asignar Plan)
   */
  startCompanyOnboarding(): void {
    if (this.thirdFormGroup.invalid || this.isAssigningPlan) {
      return;
    }

    const selectedPlan = this.getSelectedPlan();
    const storageInGigabytes = selectedPlan?.limits?.storage;

    if (!selectedPlan?.id || storageInGigabytes === undefined || storageInGigabytes === null) {
      this.thirdFormGroup.get('planId')?.setErrors({ invalidPlanConfiguration: true });
      this.snackBar.open(
        this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.ERROR'),
        this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
        {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        }
      );
      return;
    }

    this.isAssigningPlan = true;

    const request = this.buildOnboardingRequest();
    const onboarding$ = this.createdCompanyId
      ? of<StartCompanyOnboardingResponse>({ companyId: this.createdCompanyId })
      : this.companiesService.v1CompaniesApiCompanyOnboardingStartPost(request);

    onboarding$.pipe(
      switchMap((response: StartCompanyOnboardingResponse) => {
        const companyId = response.companyId;
        if (!companyId) {
          return throwError(() => new Error('Company onboarding did not return a companyId'));
        }

        // Guardar el ID antes de configurar el plan permite reintentar una
        // sincronización fallida sin crear una segunda empresa.
        this.createdCompanyId = companyId;

        return this.companyPlanLimitsService.saveConfiguration(
          companyId,
          request.name ?? request.legalName ?? companyId,
          this.buildAssignPlanRequest(selectedPlan),
          storageInGigabytes
        ).pipe(map(() => response));
      })
    ).subscribe({
      next: (response: StartCompanyOnboardingResponse) => {
        this.createdCompanyId = response.companyId || null;
        this.progress = 60;
        this.isAssigningPlan = false;

        // El estado de Operador de Datos se persiste con un endpoint separado,
        // igual que en la edición de empresa, una vez creada la empresa.
        this.saveDataOperatorStatusForCreatedCompany();

        // Avanzar al siguiente paso
        if (this.stepper) {
          this.stepper.next();
        }

      },
      error: async (error) => {
        console.error('Error al crear empresa:', error);
        this.isAssigningPlan = false;

        // Usar ErrorService para traducir el error
        const errorMessage = await this.errorService.getTranslatedError(error);

        this.snackBar.open(
          errorMessage,
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  /**
   * Persiste el estado de Operador de Datos de la empresa recién creada.
   * Solo se llama si la empresa comparte datos y se marcó el operador.
   */
  private saveDataOperatorStatusForCreatedCompany(): void {
    const isDataOperator = this.firstFormGroup.get('isDataOperator')?.value ?? false;
    if (!this.createdCompanyId || !isDataOperator) {
      return;
    }

    const request: SetDataOperatorStatusRequest = { isDataOperator };
    this.companiesService
      .v1CompaniesApiCompaniesCompanyIdDataOperatorStatusPut(this.createdCompanyId, request)
      .subscribe({
        next: () => { },
        error: async (error) => {
          const errorMessage = await this.errorService.getTranslatedError(error);
          this.snackBar.open(
            errorMessage,
            this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
  }

  inviteUsers(): void {
    const inviteChoice = this.fifthFormGroup.get('inviteChoice')?.value;
    if (inviteChoice !== 'yes') {
      this.finishWithoutInvites();
      return;
    }

    if (this.fifthFormGroup.invalid || this.emailsList.length === 0 || !this.createdCompanyId) {
      return;
    }

    this.isInvitingUsers = true;

    // Obtener el idioma del usuario o usar español por defecto
    const language = this.firstFormGroup.get('langCode')?.value || 'es';

    const request: InviteUsersToCompanyRequest = {
      userEmails: this.emailsList,
      langCode: language
    };

    this.companiesService.v1CompaniesApiCompanyOnboardingCompanyIdUsersInvitePost(
      this.createdCompanyId,
      request
    ).subscribe({
      next: () => {
        this.isInvitingUsers = false;
        this.finishOnboardingSuccess(this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.SUCCESS_WITH_INVITES'));
      },
      error: async (error) => {
        console.error('Error invitando usuarios:', error);
        this.isInvitingUsers = false;

        // Usar ErrorService para traducir el error
        const errorMessage = await this.errorService.getTranslatedError(error);

        this.snackBar.open(
          errorMessage,
          this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  /**
   * Finaliza el asistente marcando éxito y cerrando el diálogo
   */
  finishOnboardingSuccess(message?: string): void {
    const successMessage = message ?? this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.SUCCESS');

    this.progress = 100;
    this.snackBar.open(
      successMessage,
      this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.CLOSE_TOOLTIP'),
      {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['success-snackbar']
      }
    );

    this.close();
  }

  /**
   * Finaliza sin enviar invitaciones cuando no hay correos
   */
  finishWithoutInvites(): void {
    if (!this.createdCompanyId || this.isInvitingUsers) {
      return;
    }
    this.finishOnboardingSuccess(this.translate.instant('ADMINISTRATION.DIALOGS.NEW_COMPANY.SUCCESS'));
  }

  close(): void {
    this.dialogRef.close();
  }

  onStepChange(previousIndex: number, currentIndex: number): void {
    // Si vamos hacia adelante
    if (currentIndex > previousIndex) {
      // Paso 0 → 1: Incrementar a 20%
      if (previousIndex === 0 && currentIndex === 1) {
        this.progress = 20;
      }
      // Paso 1 → 2: Incrementar a 40%
      else if (previousIndex === 1 && currentIndex === 2) {
        this.progress = 40;
      }
      // Paso 2 → 3: NO incrementamos aquí, se hace en startCompanyOnboarding (60%)
      // Paso 3 → 4: Incrementar a 80%
      else if (previousIndex === 3 && currentIndex === 4) {
        this.progress = 80;
      }
    }
    // Si vamos hacia atrás
    else if (currentIndex < previousIndex) {
      // Paso 4 → 3: Decrementar a 60%
      if (previousIndex === 4 && currentIndex === 3) {
        this.progress = 60;
      }
      // Paso 3 → 2: Decrementar a 40%
      else if (previousIndex === 3 && currentIndex === 2) {
        this.progress = 40;
      }
      // Paso 2 → 1: Decrementar a 20%
      else if (previousIndex === 2 && currentIndex === 1) {
        this.progress = 20;
      }
      // Paso 1 → 0: Decrementar a 0%
      else if (previousIndex === 1 && currentIndex === 0) {
        this.progress = 0;
      }
    }
  }
}
